import { Combobox, Skeleton } from '@ibsys/kit';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertReportVersions } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { Icon, IconButton } from '@/shared/ui';

export type ExpertReportVersionDropdownProps = {
  reportId?: number;
  value?: number;
  onChange?: (value?: number) => void;
  className?: string;
  disabled?: boolean;
};

const formatDate = (createdAt: string) => format(new Date(createdAt), 'dd.MM.yyyy HH:mm');

export const ExpertReportVersionDropdown: FC<ExpertReportVersionDropdownProps> = (props) => {
  const { reportId, value, onChange, className, disabled } = props;
  const { t } = useTranslation('expertReport');

  const { data: versions, isLoading } = useExpertReportVersions(reportId);

  const sortedVersions = useMemo(() => {
    if (!versions) return [];
    return [...versions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [versions]);

  const lastVersion = sortedVersions[0];

  const isLastVersion = value && lastVersion?.id === value;

  const options = useMemo(() => {
    return sortedVersions.map((version, index) => ({
      value: version.id,
      label: (
        <span>
          {formatDate(version.createdAt)}{' '}
          {!index && <span className="text-sm text-muted-foreground">{`(${t('currentVersion')})`}</span>}
        </span>
      ),
    }));
  }, [sortedVersions, t]);

  const version = useMemo(() => {
    return sortedVersions?.find((v) => v.id === value);
  }, [sortedVersions, value]);

  const handleClickBack = () => {
    lastVersion && onChange?.(lastVersion?.id);
  };

  const handleShowPreviousVersion = () => {
    sortedVersions[1] && onChange?.(sortedVersions[1].id);
  };

  if (isLoading || !versions) {
    return <Skeleton className={cn('w-40 h-6', className)} />;
  }

  return (
    <div className={cn('flex gap-4 items-center', className)}>
      {isLastVersion && sortedVersions.length > 1 && (
        <IconButton
          label={t('showPreviousVersion')}
          icon={<Icon type="history" style={{ width: 20 }} />}
          className="text-foreground size-7"
          variant="ghost"
          onClick={handleShowPreviousVersion}
        />
      )}
      {!isLastVersion && (
        <>
          <IconButton
            label={t('backToCurrentVersion')}
            icon={<Icon type="arrow-long-left" />}
            variant="ghost"
            className="size-7"
            onClick={handleClickBack}
          />

          <Combobox
            options={options}
            value={value}
            menu={{
              className: 'min-w-56',
            }}
            onChange={(v) => onChange?.(v as number)}
            disabled={disabled || isLoading}
          >
            {!version ? null : (
              <div className="text-sm font-medium flex items-center gap-3 whitespace-nowrap">
                {formatDate(version.createdAt)} <Icon type="arrow-down" width={12} className="shrink-0" />
              </div>
            )}
          </Combobox>
        </>
      )}
    </div>
  );
};
